import { useContext, useEffect, useState } from 'react';
import { useFilterContext } from '../contexts/FilterContext';
import { mapModes } from '../constants/map';
import { MapContext } from '../contexts/MapContext';
import { fitSectionGroupBounds } from '../utils/map';

// Sets the active section group when there is a section group filter on
// initial load and updates the section group filter with the active
// section group when it changes.
//
// Pass in the section groups array only if section groups are loaded.
// If they are not yet loaded, pass in a falsey value.
// This hook will only run once the section groups are loaded.
const useSyncActiveSectionGroup = (sectionGroups) => {
  const [initialized, setInitialized] = useState(false);
  const {
    state: { sectionGroupId: contextSectionGroupId },
    updateFilter,
  } = useFilterContext();
  const {
    fields: { fieldSections },
    mapState: { setMapMode, activeSectionGroupId, mapRef, mapLoaded },
  } = useContext(MapContext);

  useEffect(() => {
    // Only do this the first time after section groups are loaded.
    if (!sectionGroups || !mapLoaded || initialized) return;
    setInitialized(true);
    if (!contextSectionGroupId) return;
    const sectionGroup = sectionGroups.find(
      ({ id }) => id === contextSectionGroupId,
    );
    if (sectionGroup) {
      // Apply the section group filter to the map.
      setMapMode(mapModes.SECTION_GROUP_ENGAGED, contextSectionGroupId, {
        sectionGroup,
      });
      if (mapRef.current) {
        fitSectionGroupBounds(sectionGroup, fieldSections, mapRef);
      }
    } else {
      // Section groups are loaded, but the filter is set to a section group
      // that doesn't exist so reset the filter.
      updateFilter('sectionGroupId', '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sectionGroups, mapLoaded]);

  useEffect(() => {
    if (!initialized) return;
    if (activeSectionGroupId !== contextSectionGroupId) {
      updateFilter('sectionGroupId', activeSectionGroupId || '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeSectionGroupId, initialized]);
};

export default useSyncActiveSectionGroup;
